import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, AlertOctagon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { complianceMonitorService } from '@/services/complianceMonitor/complianceMonitor.service';
import type {
  ActionHorizon,
  Reason,
  SoDeliverySimulationDetail,
  SoDeliverySimulationItem,
} from '@/services/complianceMonitor/complianceMonitor.types';
import { ActionHorizonPill } from './ActionHorizonPill';
import { TablePaginationBar } from '@/components/shared/TablePaginationBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';

interface Props {
  simulationId: number | null;
  salesOrderId?: number | null;
  isOpen: boolean;
  onClose: () => void;
  onReviewedChange?: (simulationId: number, reviewed: boolean) => void;
}

function toIsoWeekLabel(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  const utcDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${utcDate.getUTCFullYear()}-S${String(weekNo).padStart(2, '0')}`;
}

function todayIsoWeekLabel(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return toIsoWeekLabel(`${y}-${m}-${day}`);
}

function ReasonBadge({ reason }: { reason: Reason | null | undefined }) {
  const { t } = useTranslation();
  if (!reason || reason === 'OK') return null;
  const isCritical = reason === 'LEAD_TIME_EXCEEDS_DEADLINE' || reason === 'ETA_AFTER_NEED';
  const cls = isCritical
    ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-amber-50 text-amber-800 border-amber-200';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border whitespace-nowrap ${cls}`}
    >
      {t(`complianceMonitor.reason.${reason}`)}
    </span>
  );
}

function BottleneckChip() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border whitespace-nowrap bg-purple-50 text-purple-800 border-purple-200">
      <AlertOctagon className="w-3 h-3" />
      {t('complianceMonitor.item.bottleneck')}
    </span>
  );
}

function SlackBadge({ slack }: { slack: number | null | undefined }) {
  const { t } = useTranslation();
  if (slack === null || slack === undefined) return null;
  if (slack > 0) {
    return (
      <span className="text-[11px] text-green-700 font-medium">
        {t('complianceMonitor.item.slackPositive', { count: slack })}
      </span>
    );
  }
  if (slack === 0) {
    return (
      <span className="text-[11px] text-amber-700 font-medium">
        {t('complianceMonitor.item.slackZero')}
      </span>
    );
  }
  return (
    <span className="text-[11px] text-red-700 font-medium">
      {t('complianceMonitor.item.slackNegative', { count: Math.abs(slack) })}
    </span>
  );
}

function deriveItemHorizon(item: SoDeliverySimulationItem): ActionHorizon {
  if (item.action_horizon) return item.action_horizon;
  // Fallback retrocompatible si el backend no lo emite (no deberia pasar tras bloque 1).
  return item.component_feasible ? 'OK' : 'NOT_FEASIBLE';
}

function ItemRoleTags({ role }: { role?: SoDeliverySimulationItem['planning_item_role'] }) {
  const { t } = useTranslation();
  const pill = 'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border whitespace-nowrap';
  const lineCls = `${pill} bg-emerald-50 text-emerald-800 border-emerald-200`;
  const compCls = `${pill} bg-amber-50 text-amber-900 border-amber-200`;

  if (role === 'order_line') {
    return <span className={lineCls}>{t('complianceMonitor.item.role.orderLine')}</span>;
  }
  if (role === 'mixed') {
    return (
      <>
        <span className={lineCls}>{t('complianceMonitor.item.role.orderLine')}</span>
        <span className={compCls}>{t('complianceMonitor.item.role.component')}</span>
      </>
    );
  }
  return <span className={compCls}>{t('complianceMonitor.item.role.component')}</span>;
}

function ItemRow({ item }: { item: SoDeliverySimulationItem }) {
  const { t } = useTranslation();
  const horizon = deriveItemHorizon(item);

  const supplyTypeLabel =
    item.supply_type_description || (item.on_demand ? t('complianceMonitor.item.onDemand') : t('complianceMonitor.item.notOnDemand'));

  const showReason = item.reason && item.reason !== 'OK';
  const showMaterialReadyBy = horizon !== 'OK' && Boolean(item.material_ready_by_date);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-mono text-sm font-semibold text-gray-900 shrink-0">{item.component_product_code}</span>
            <span className="text-sm text-gray-600 truncate">{item.component_product_name}</span>
          </div>
          {item.supplier_name ? (
            <div className="mt-1 text-xs">
              <span className="text-gray-500">{t('complianceMonitor.item.supplier')}</span>
              <span className="font-medium text-gray-900"> · {item.supplier_name}</span>
            </div>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <ItemRoleTags role={item.planning_item_role} />
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border bg-blue-50 text-blue-700 border-blue-200 whitespace-nowrap">
              {supplyTypeLabel}
            </span>
            {item.is_bottleneck && <BottleneckChip />}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <ActionHorizonPill horizon={horizon} />
          {showReason && <ReasonBadge reason={item.reason} />}
          <SlackBadge slack={item.slack_days} />
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-gray-700">
        <div className="flex flex-wrap gap-2">
          <span className="text-gray-500">{t('complianceMonitor.item.qtyForOrder')}:</span>
          <span className="font-medium text-gray-900">{item.qty_for_sales_order ?? '—'}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-gray-500">{t('complianceMonitor.item.stockActual')}:</span>
          <span className="font-medium text-gray-900">{item.stock_actual ?? 0}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-gray-500">{t('complianceMonitor.item.stockInTransit')}:</span>
          <span className="font-medium text-gray-900">{item.stock_in_transit ?? 0}</span>
        </div>
        {item.on_demand && item.purchase_order_number && (
          <div className="flex flex-wrap gap-2">
            <span className="text-gray-500">{t('complianceMonitor.item.purchaseOrderNumber')}:</span>
            <span className="font-medium text-gray-900">{item.purchase_order_number}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <span className="text-gray-500">{t('complianceMonitor.item.lastDemandWeek')}:</span>
          <span className="font-mono font-medium text-gray-900">{toIsoWeekLabel(item.last_demand_week_bucket)}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-gray-500">{t('complianceMonitor.item.leadTime')}:</span>
          <span className="font-medium text-gray-900">
            {item.lead_time_used_days != null
              ? t('complianceMonitor.item.leadTimeDays', { count: item.lead_time_used_days })
              : '—'}
          </span>
        </div>
        {item.recommended_plan_week_bucket_for_component && (
          <div className="flex flex-wrap gap-2">
            <span className="text-gray-500">{t('complianceMonitor.item.recommendedPlanWeek')}:</span>
            <span className="font-medium text-gray-900">{toIsoWeekLabel(item.recommended_plan_week_bucket_for_component)}</span>
          </div>
        )}
        {showMaterialReadyBy && (
          <div className="flex flex-wrap gap-2">
            <span className="text-gray-500">{t('complianceMonitor.item.materialReadyBy')}:</span>
            <span className="font-medium text-gray-900">
              {new Date(item.material_ready_by_date as string).toLocaleDateString()}
            </span>
          </div>
        )}
        {item.notes && (
          <div className="flex items-start gap-2 text-gray-600">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
            <span className="text-xs">{item.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function SoDeliverySimulationSidebar({ simulationId, salesOrderId, isOpen, onClose, onReviewedChange }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<SoDeliverySimulationDetail | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'feasible' | 'not_feasible'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);

  useEffect(() => {
    if (!isOpen || (!simulationId && !salesOrderId)) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    const detailPromise =
      simulationId && simulationId > 0
        ? complianceMonitorService.getSoDeliverySimulation(simulationId)
        : complianceMonitorService.getProgrammedRiskDetail(Number(salesOrderId));

    detailPromise
      .then((d) => {
        if (!cancelled) {
          setDetail(d);
          setSearchCode('');
          setStatusFilter('all');
          setPage(1);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, simulationId, salesOrderId]);

  const headerTitle = useMemo(() => {
    if (!detail) return t('complianceMonitor.sidebar.title');
    return `${t('complianceMonitor.sidebar.title')} · ${detail.order_number}`;
  }, [detail, t]);

  const filteredItems = useMemo(() => {
    if (!detail) return [];
    const q = searchCode.trim().toLowerCase();
    let items = detail.items;
    if (q) {
      items = items.filter((it) => (it.component_product_code || '').toLowerCase().includes(q));
    }
    if (statusFilter === 'feasible') {
      items = items.filter((it) => deriveItemHorizon(it) === 'OK');
    } else if (statusFilter === 'not_feasible') {
      items = items.filter((it) => deriveItemHorizon(it) !== 'OK');
    }
    return items;
  }, [detail, searchCode, statusFilter]);

  const totalPages = useMemo(() => {
    if (!filteredItems.length) return 1;
    return Math.ceil(filteredItems.length / pageSize);
  }, [filteredItems.length]);

  const paginatedItems = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, totalPages]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[90] bg-black/20" onClick={onClose} aria-hidden />
      <aside className="fixed right-0 top-0 z-[91] h-full w-full max-w-[520px] bg-white shadow-2xl border-l border-gray-200 flex flex-col">
        <div className="px-4 py-2 border-b border-gray-200 bg-gray-100 shrink-0 flex items-center justify-between gap-2 text-xs text-gray-800">
          <span className="font-medium">{t('complianceMonitor.sidebar.currentIsoWeek')}</span>
          <span className="font-mono font-semibold tabular-nums">{todayIsoWeekLabel()}</span>
        </div>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{headerTitle}</div>
            {detail && (
              <div className="mt-0.5 text-xs text-gray-500">
                {t('complianceMonitor.sidebar.expectedDelivery')}: <span className="font-medium text-gray-800">{toIsoWeekLabel(detail.expected_delivery_date)}</span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded hover:bg-gray-100 text-gray-600"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          {detail ? (
            <ActionHorizonPill horizon={detail.action_horizon} size="md" />
          ) : (
            <span className="text-xs text-gray-500">{t('common.loading')}</span>
          )}

          {detail && simulationId && simulationId > 0 && (
            <button
              type="button"
              disabled={reviewBusy}
              onClick={async () => {
                if (!simulationId || simulationId <= 0) return;
                setReviewBusy(true);
                try {
                  const next = !detail.reviewed;
                  await complianceMonitorService.markSoDeliverySimulationReviewed(simulationId, next);
                  setDetail({ ...detail, reviewed: next, reviewed_at: next ? new Date().toISOString() : null });
                  onReviewedChange?.(simulationId, next);
                } finally {
                  setReviewBusy(false);
                }
              }}
              className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {detail.reviewed ? t('complianceMonitor.actions.markUnreviewed') : t('complianceMonitor.actions.markReviewed')}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {loading && (
            <div className="text-sm text-gray-500">{t('common.loading')}</div>
          )}
          {!loading && detail && (
            <div className="mb-3 flex flex-col md:flex-row gap-2">
              <input
                type="text"
                value={searchCode}
                onChange={(e) => {
                  setSearchCode(e.target.value);
                  setPage(1);
                }}
                placeholder={t('complianceMonitor.sidebar.searchCode')}
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1681ba] focus:border-[#1681ba]"
              />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as 'all' | 'feasible' | 'not_feasible');
                  setPage(1);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1681ba] focus:border-[#1681ba]"
              >
                <option value="all">{t('complianceMonitor.sidebar.filters.all')}</option>
                <option value="feasible">{t('complianceMonitor.sidebar.filters.feasible')}</option>
                <option value="not_feasible">{t('complianceMonitor.sidebar.filters.notFeasible')}</option>
              </select>
            </div>
          )}
          {!loading && detail && filteredItems.length === 0 && (
            <div className="text-sm text-gray-500">{t('common.noData')}</div>
          )}
          {!loading && detail && filteredItems.length > 0 && (
            <div className="space-y-3">
              {paginatedItems.map((it) => (
                <ItemRow key={it.id} item={it} />
              ))}
              <TablePaginationBar
                currentPage={Math.min(Math.max(page, 1), totalPages)}
                totalPages={totalPages}
                totalItems={filteredItems.length}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
}

